import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { RailwayZones } from '../../../station-track-visualizer/models/model';
import { StationSurveyStepperService } from './station-survey-stepper.service';
import { isNullOrUndefined } from '../../../../../../usermgmt/src/lib/modules/shared';
import {
  SCOPE_EVERYWHERE,
  SCOPE_HERE,
  SCOPE_CANCEL,
  SyncScopeChoice,
  StationMetaScopes,
  resolveStationMetaScopes,
} from '../../../constants/scopeConstants';

export {
  SCOPE_EVERYWHERE,
  SCOPE_HERE,
  SCOPE_CANCEL,
  SyncScopeChoice,
  StationMetaScopes,
  resolveStationMetaScopes,
};

export interface SyncScopeDialogState {
  visible: boolean;
  fieldName: string;
  oldValue: string;
  newValue: string;
  resolve?: (choice: SyncScopeChoice) => void;
}

/**
 * Table persistence key aliases to support legacy and current table identifiers.
 */
export const TABLE_PERSIST_KEY_ALIASES: Record<string, string[]> = {
  author_table: ['author_table', 'author_review_table'],
  author_review_table: ['author_table', 'author_review_table'],
  signal_telecommunication_double_data_table: [
    'signal_telecommunication_double_data_table',
    'ignal_telecommunication_double_data_table',
  ],
  ignal_telecommunication_double_data_table: [
    'signal_telecommunication_double_data_table',
    'ignal_telecommunication_double_data_table',
  ],
  station_info_table: ['station_info_table', 'station_tag_summary'],
  station_tag_summary: ['station_info_table', 'station_tag_summary'],
};

/**
 * Determines whether a specific table has user-generated or user-persisted modifications.
 *
 * @param tableKey Key identifying the table.
 * @param stationData Station data container holding table_persist_info.
 * @returns boolean True if user persisted, False otherwise.
 */
export function isTablePersistedByUser(tableKey: string, stationData?: any): boolean {
  if (!tableKey || !stationData) return false;
  const p = stationData.table_persist_info;
  const keys = TABLE_PERSIST_KEY_ALIASES[tableKey] ?? [tableKey];
  if (p) {
    if (keys.some((k) => p[k] === 'persist_system_generated')) {
      return false;
    }
    if (keys.some((k) => p[k] === 'persist_user_generated' || p[k] === 'persist_user_change')) {
      return true;
    }
  }
  if ((tableKey === 'station_info_table' || tableKey === 'station_tag_summary') &&
      stationData.station_tag_summary?.station_tag_table_persist?.persist_user_change) {
    return true;
  }
  return false;
}

/**
 * Builds the canonical station title.
 * When hideCode is true, only stationName is returned.
 * When hideCode is false, returns 'StationName (StationCode)'.
 *
 * @param stationName Display name of station.
 * @param stationCode Optional station code.
 * @param hideCode When true, omits code from title.
 * @returns Formatted station title string.
 */
export function formatStationTitle(
  stationName: string | null | undefined,
  stationCode: string | null | undefined,
  hideCode: boolean = false
): string {
  const name = (stationName ?? '').trim();
  const code = (stationCode ?? '').trim();
  if (!name) return '';
  const cleanName = name.includes('(') ? name.split('(')[0].trim() : name;
  if (hideCode || !code) {
    return cleanName;
  }
  return `${cleanName} (${code})`;
}

/**
 * Shared mixin for table components that support 'here'/'everywhere' scope editing.
 * Provides the user-generated check and table persistence.
 */
export class ScopeEditMixin {
  protected get tableKey(): string { return ''; }
  protected get stationSyncService(): StationDetailsSyncService { return (this as any).stationIdSyncService; }
  protected get tableStationData(): any { return undefined; }

  /**
   * Checks whether this table has user-persisted changes that must NOT be auto-synced.
   *
   * @returns boolean True if table is marked user-generated or user-changed.
   */
  isUserGenerated(): boolean {
    return isTablePersistedByUser(this.tableKey, this.tableStationData);
  }

  /**
   * Marks this table as user-persisted in table_persist_info.
   */
  protected markTablePersisted(): void {
    if (!this.tableStationData) return;
    if (!this.tableStationData.table_persist_info) this.tableStationData.table_persist_info = {};
    this.tableStationData.table_persist_info[this.tableKey] = 'persist_user_change';
  }

  /**
   * Clears this table's persistence lock from table_persist_info.
   */
  protected clearTablePersistence(): void {
    if (this.tableStationData?.table_persist_info) {
      delete this.tableStationData.table_persist_info[this.tableKey];
    }
  }
}

export function purgeRootScopeFields(stationData: any): void {
  if (!stationData || typeof stationData !== 'object') return;
  delete stationData.station_id_scope;
  delete stationData.station_code_scope;
  delete stationData.station_name_scope;
  delete stationData.station_title_scope;
  delete stationData.update_scope;
  delete stationData.station_sync_scope;
}

@Injectable()
export class StationDetailsSyncService {
  constructor(private readonly stepperService: StationSurveyStepperService) {}

  purgeRootScopeFields(stationData: any): void {
    purgeRootScopeFields(stationData);
  }

  /**
   * Ensures station_meta_scopes object exists inside station_meta_info on the station_title asset
   * and updates the specified field scope ('everywhere' or 'here').
   * Purges all loose scope properties from the root of stationData so they are never in global scope.
   *
   * @param stationData Target station data container.
   * @param fieldKeyOrScopes Field identifier ('station_id', 'station_code', etc.) or object of scopes.
   * @param scopeVal String constant ('everywhere', 'here', 'cancel') or boolean.
   */
  ensureStationMetaScopes(
    stationData: any,
    fieldKeyOrScopes?: string | Partial<StationMetaScopes>,
    scopeVal: SyncScopeChoice | boolean = SCOPE_EVERYWHERE
  ): void {
    if (!stationData) return;

    const normalizedScope: SyncScopeChoice =
      scopeVal === false || scopeVal === SCOPE_HERE
        ? SCOPE_HERE
        : scopeVal === SCOPE_CANCEL
        ? SCOPE_CANCEL
        : SCOPE_EVERYWHERE;

    const metaList = Array.isArray(stationData.station_meta_info)
      ? stationData.station_meta_info
      : Array.isArray(stationData)
      ? stationData
      : null;

    let titleAsset: any = null;
    if (metaList) {
      titleAsset = metaList.find((m: any) => m && m.type_of_asset === 'station_title');
      if (!titleAsset && Array.isArray(stationData.station_meta_info)) {
        titleAsset = {
          type_of_asset: 'station_title',
          station_name: stationData['Station Name'] || stationData.station_name || '',
          station_id: stationData['Station ID'] || stationData.station_id || '',
          station_code: stationData['Station Code'] || stationData.station_code || '',
          station_title: stationData['Station Title'] || stationData.station_title || '',
          station_meta_scopes: resolveStationMetaScopes(stationData),
          modified_assets: {}
        };
        stationData.station_meta_info.push(titleAsset);
      }
    }

    if (titleAsset) {
      if (!titleAsset.station_meta_scopes || typeof titleAsset.station_meta_scopes !== 'object') {
        titleAsset.station_meta_scopes = resolveStationMetaScopes(stationData);
      }
      if (typeof fieldKeyOrScopes === 'object' && fieldKeyOrScopes !== null) {
        Object.entries(fieldKeyOrScopes).forEach(([k, v]) => {
          const prop = k.endsWith('_scope') ? k : `${k}_scope`;
          const normVal: SyncScopeChoice =
            (v as any) === false || v === SCOPE_HERE
              ? SCOPE_HERE
              : v === SCOPE_CANCEL
              ? SCOPE_CANCEL
              : SCOPE_EVERYWHERE;
          titleAsset.station_meta_scopes[prop] = normVal;
          if (titleAsset.modified_assets) {
            if (!titleAsset.modified_assets.station_meta_scopes) {
              titleAsset.modified_assets.station_meta_scopes = { ...titleAsset.station_meta_scopes };
            }
            titleAsset.modified_assets.station_meta_scopes[prop] = normVal;
          }
        });
      } else if (typeof fieldKeyOrScopes === 'string') {
        const scopeProp = fieldKeyOrScopes.endsWith('_scope') ? fieldKeyOrScopes : `${fieldKeyOrScopes}_scope`;
        titleAsset.station_meta_scopes[scopeProp] = normalizedScope;
        if (titleAsset.modified_assets) {
          if (!titleAsset.modified_assets.station_meta_scopes) {
            titleAsset.modified_assets.station_meta_scopes = { ...titleAsset.station_meta_scopes };
          }
          titleAsset.modified_assets.station_meta_scopes[scopeProp] = normalizedScope;
        }
      }
    }

    // Clean up any loose root scope fields so they never live in global scope
    purgeRootScopeFields(stationData);
    if (this.stepperService.surveyData?.stationData) {
      purgeRootScopeFields(this.stepperService.surveyData.stationData);
    }
  }

  /**
   * Checks whether the specified table is user-generated or user-persisted.
   * Tables marked as user-generated should NEVER be prompted with confirmation modals,
   * nor overwritten by automatic synchronization across tables.
   *
   * @param tableKey Name/key of the table.
   * @param stationData Optional stationData; defaults to stepperService.stationData.
   * @returns boolean True if table is user generated / user persisted.
   */
  isTableUserGenerated(tableKey: string, stationData?: any): boolean {
    return isTablePersistedByUser(tableKey, stationData || this.stepperService.stationData);
  }

  /**
   * PIM edits of station Name / Code / ID wait here (no pop-up) until Update or Regenerate is clicked.
   * Keyed per field so repeated edits keep the first old value and the latest new value.
   */
  private readonly pendingPimChanges = new Map<string, {
    oldValue: string;
    newValue: string;
    order: number;
    apply: (newValue: string, oldValue: string) => void;
  }>();

  /**
   * Queues a PIM change to be applied everywhere on Update / Regenerate.
   * Changing a value back to its original drops the pending change.
   *
   * @param key Unique field key, e.g. 'main:name' or 'adj:<uuid>:id'.
   * @param oldValue Value before the edit.
   * @param newValue Value after the edit.
   * @param apply Applies the change everywhere.
   * @param order Lower runs first (IDs run first because the form already holds the new ID).
   */
  queuePimChange(
    key: string,
    oldValue: string,
    newValue: string,
    apply: (newValue: string, oldValue: string) => void,
    order = 0
  ): void {
    const original = this.pendingPimChanges.get(key)?.oldValue ?? String(oldValue ?? '').trim();
    const next = String(newValue ?? '').trim();
    if (!original || !next || next === original) {
      this.pendingPimChanges.delete(key);
      return;
    }
    this.pendingPimChanges.set(key, { oldValue: original, newValue: next, order, apply });
  }

  /**
   * Applies every queued PIM change everywhere, skipping user-generated tables (handled by each sync).
   */
  applyPendingPimChanges(): void {
    const changes = [...this.pendingPimChanges.entries()].sort(([, a], [, b]) => a.order - b.order);
    if (!changes.length) return;
    this.pendingPimChanges.clear();
    changes.forEach(([key, c]) => {
      c.apply(c.newValue, c.oldValue);
      this.appliedPimChanges.push({ key, oldValue: c.oldValue, newValue: c.newValue });
    });
    // Preview Layout regenerates so it shows the new values
    this.stepperService.trackRegenerationReqired = true;
  }

  /** Name / ID / Code changes already applied, in apply order (keys as in queuePimChange). */
  private readonly appliedPimChanges: { key: string; oldValue: string; newValue: string }[] = [];

  /**
   * A draft layout fetched from the server after the edits were applied still has the old values.
   * Puts the applied changes into it before it is sent; a field that no longer holds the old value is left alone.
   */
  applyAppliedPimChangesTo(layout: any): void {
    if (!layout || typeof layout !== 'object') return;
    const same = (a: any, b: any) => String(a ?? '').trim() === String(b ?? '').trim();
    this.appliedPimChanges.forEach(({ key, oldValue: ov, newValue: nv }) => {
      const [kind, ...rest] = key.split(':');
      if (kind === 'main') {
        const field = rest[0];
        if (field === 'name' && same(layout['Station Name'], ov)) this.syncStationName(layout, nv, ov, true);
        else if (field === 'id' && same(layout['Station ID'], ov)) this.ssyncStationId(layout, ov, nv, true);
        else if (field === 'code' && same(layout['Station Code'], ov)) this.syncStationCode(layout, nv, ov, true);
        return;
      }
      const [index, field] = rest;
      const uuid = this.stepperService.surveyData?.adjacentStations?.[Number(index)]?.station_uuid;
      const list = Array.isArray(layout.adjacent_stations) ? layout.adjacent_stations : [];
      const adj = (uuid && list.find((a: any) => a?.station_uuid === uuid)) || list[Number(index)];
      if (!adj || !same(adj[`station_${field}`], ov)) return;
      if (field === 'name') this.syncAdjacentStationName(layout, nv, adj.station_uuid, adj.station_id, ov, true);
      else if (field === 'id') this.syncAdjacentStationId(layout, nv, adj.station_uuid, ov, true);
      else if (field === 'code') this.syncAdjacentStationCode(layout, nv, adj.station_uuid, adj.station_id, ov, true);
    });
  }

  /**
   * Emits after every station ID / Name / Code sync so that
   * sibling components (IP-port table, tag-summary table, visualizer)
   * can call detectChanges() and instantly reflect the new values.
   */
  readonly stationDetailsUpdated$ = new Subject<void>();

  /**
   * Emits state changes for the scope confirmation popup.
   */
  readonly scopeDialogState$ = new BehaviorSubject<SyncScopeDialogState>({
    visible: false,
    fieldName: '',
    oldValue: '',
    newValue: ''
  });

  /**
   * Prompts the user with the 3-button confirmation popup:
   * 'Update here' (SCOPE_HERE), 'Update everywhere' (SCOPE_EVERYWHERE), or 'Cancel' (SCOPE_CANCEL).
   *
   * @param fieldName Label of the field being changed (e.g. 'Station Name', 'Station ID', 'Station Code').
   * @param oldValue Current value before edit.
   * @param newValue Proposed new value after edit.
   * @returns Promise resolving to chosen SyncScopeChoice.
   */
  requestSyncScope(fieldName: string, oldValue: string, newValue?: string): Promise<SyncScopeChoice> {
    const oldNorm = String(oldValue ?? '').trim();
    const newNorm = newValue !== undefined ? String(newValue ?? '').trim() : '';

    return new Promise<SyncScopeChoice>((resolve) => {
      this.scopeDialogState$.next({
        visible: true,
        fieldName,
        oldValue: oldNorm,
        newValue: newNorm,
        resolve: (choice: SyncScopeChoice) => {
          this.scopeDialogState$.next({
            visible: false,
            fieldName: '',
            oldValue: '',
            newValue: ''
          });
          resolve(choice);
        }
      });
    });
  }

  /**
   * Builds the canonical station title using the shared formatStationTitle helper.
   *
   * @param stationName Display name of station.
   * @param stationCode Optional station code.
   * @param hideCode When true, omits code from title.
   * @returns Formatted station title string.
   */
  formatStationTitle(
    stationName: string | null | undefined,
    stationCode: string | null | undefined,
    hideCode: boolean = false
  ): string {
    return formatStationTitle(stationName, stationCode, hideCode);
  }

  private getAdjacentStationsList(stationData: any): any[] {
    if (!stationData) return [];
    if (Array.isArray(stationData.adjacent_stations)) {
      return stationData.adjacent_stations;
    }
    if (Array.isArray(stationData.adjacentStations)) {
      return stationData.adjacentStations;
    }
    if (stationData.adjacent_stations && typeof stationData.adjacent_stations === 'object') {
      return Object.values(stationData.adjacent_stations);
    }
    if (Array.isArray(this.stepperService?.surveyData?.adjacentStations)) {
      return this.stepperService.surveyData.adjacentStations;
    }
    return [];
  }

  private findAdjacentByUuidOrId(
    stationData: any,
    uuid: string | null | undefined,
    id: string | null | undefined
  ): any {
    const list = this.getAdjacentStationsList(stationData);
    return list.find((s: any) => {
      if (uuid && s.station_uuid === uuid) return true;
      if (id && String(s.station_id) === String(id)) return true;
      return false;
    }) ?? null;
  }

  // Checks if given stationId matches the current station identity
  public isCurrentStation(stationData: any, stationId: string): boolean {
    // Return false if stationData or stationId is missing
    if (!stationData || !stationId) return false;
    // Compare string representations of current station ID
    return String(stationData?.['Station ID']) === String(stationId);
  }

  // Finds adjacent station entry by matching station ID
  public findAdjacentById(stationData: any, stationId: string): any {
    const list = this.getAdjacentStationsList(stationData);
    return list.find(
      (s: any) => String(s?.station_id) === String(stationId)
    ) ?? null;
  }

  /**
   * Determine which field populates the `name` column in station_tag_summary
   * for a given zone / station type, mirroring _resolve_station_display_name
   * and _build_block_column in Python.
   *
   * Returns 'station_name' | 'station_code'
   */
  // Resolves whether name or code is displayed in tag summary based on railway zone
  public nameFieldForZone(
    // Target railway zone string
    zone: string,
    // Whether station is current main station
    isCurrent: boolean,
    // Whether station is block section station
    isBlock: boolean
  // Returns field key string
  ): 'station_name' | 'station_code' {
    // Normalize zone string to lower case
    const z = (zone ?? '').trim().toLowerCase();
    // Western railway always displays station code
    if (z === RailwayZones.WESTERN_RAILWAY.toLowerCase()) return 'station_code';
    // West central railway always displays station name
    if (z === RailwayZones.WEST_CENTRAL_RAILWAY.toLowerCase()) return 'station_name';
    // Central railway displays station name for current station and station code for others
    if (z === RailwayZones.CENTRAL_RAILWAY.toLowerCase()) return isCurrent ? 'station_name' : 'station_code';
    // Block section stations default to station code
    if (isBlock) return 'station_code';
    // Standard stations default to station name
    return 'station_name';
  }

  private addUpdateInputs(data: any): void {
    if (!data) return;
    if (!Array.isArray(data.operations_list)) {
      data.operations_list = [];
    }
    if (!data.operations_list.includes('Update Inputs')) {
      data.operations_list.push('Update Inputs');
    }
  }

  private removeUpdateInputs(data: any): void {
    if (!data || !Array.isArray(data.operations_list)) return;
    data.operations_list = data.operations_list.filter(
      (op: string) => op !== 'Update Inputs' && op !== 'update_inputs'
    );
  }

  /**
   * Records the scope chosen for a table edit and keeps 'Update Inputs' in step with it:
   * removed for 'here', added for 'everywhere' (only when operations_list exists).
   */
  applyEditScope(stationData: any, fieldKey: string, scope: SyncScopeChoice): void {
    this.ensureStationMetaScopes(stationData, fieldKey, scope);
    const ops = stationData?.operations_list;
    if (!Array.isArray(ops)) return;
    const idx = ops.indexOf('Update Inputs');
    if (scope === SCOPE_HERE) {
      if (idx !== -1) ops.splice(idx, 1);
    } else if (idx === -1) {
      ops.push('Update Inputs');
    }
  }

  /**
   * Apply Everywhere also updates the value the PIM screen shows (pim_*).
   */
  private setPimValue(stationData: any, pimKey: 'pim_station_name' | 'pim_station_id' | 'pim_station_code', newValue: any): void {
    stationData[pimKey] = newValue;
    const surveyStationData = this.stepperService.surveyData?.stationData;
    if (surveyStationData) surveyStationData[pimKey] = newValue;
  }

  private syncSurveyStationData(fields: Record<string, any>, scopeProp: string): void {
    const surveyStationData = this.stepperService.surveyData?.stationData;
    if (surveyStationData) {
      Object.assign(surveyStationData, fields);
      this.ensureStationMetaScopes(surveyStationData, scopeProp, SCOPE_EVERYWHERE);
      this.addUpdateInputs(surveyStationData);
    }
  }

  private syncToPeers(
    currentData: any,
    alreadySynced: boolean,
    syncFn: (target: any) => void
  ): void {
    if (alreadySynced) return;
    const mainData = this.stepperService.stationData;
    const draftData = this.stepperService.previewStationStore?.draftStationData;
    const surveyStationData = this.stepperService.surveyData?.stationData;

    if (currentData === mainData && draftData && draftData !== mainData) {
      syncFn(draftData);
    } else if (currentData === draftData && mainData && mainData !== draftData) {
      syncFn(mainData);
    }
    if (surveyStationData && surveyStationData !== mainData && surveyStationData !== draftData && surveyStationData !== currentData) {
      syncFn(surveyStationData);
    }
    this.stationDetailsUpdated$.next();
  }

  private updateTitleAssets(
    stationData: any,
    field: string,
    value: any,
    matchFn?: (item: any) => boolean
  ): void {
    const apply = (item: any) => {
      item[field] = value;
      if (item.modified_assets) {
        item.modified_assets[field] = value;
      }
    };
    stationData?.station_meta_info?.forEach((item: any) => {
      if (item?.type_of_asset === 'station_title' && (!matchFn || matchFn(item))) {
        apply(item);
      }
    });
    if (Array.isArray(stationData?.stationTitles)) {
      stationData.stationTitles.forEach(apply);
    }
  }

  private updateTowerFields(
    stationData: any,
    field: string,
    newVal: string,
    matches: (tower: any, key?: string) => boolean
  ): void {
    stationData?.tower_plotting_info?.forEach((tower: any) => {
      if (matches(tower)) {
        tower[field] = newVal;
      }
    });
    const towerInfo = stationData?.['Tower Information'];
    if (towerInfo) {
      Object.keys(towerInfo).forEach(key => {
        const tower = towerInfo[key];
        if (matches(tower, key)) {
          tower[field] = newVal;
        }
      });
    }
  }

  private updateSignalAndAuthorTablesTitle(
    stationData: any,
    updateTitleFn: (current: string) => string,
    stationCode?: string
  ): void {
    if (stationData?.author_review_table?.station && !this.isTableUserGenerated('author_table', stationData)) {
      if (stationCode) stationData.author_review_table.station.station_code = stationCode;
      stationData.author_review_table.station.station_name = updateTitleFn(stationData.author_review_table.station.station_name);
      stationData.author_review_table.station = { ...stationData.author_review_table.station };
      stationData.author_review_table = { ...stationData.author_review_table };
    }
    // Telecommunication tables keep their own station_code next to the title
    const setTableCode = (info: any) => {
      if (stationCode && 'station_code' in info) info.station_code = stationCode;
    };
    const dTable = stationData?.signal_telecommunication_double_data_table;
    if (dTable?.station_info && !this.isTableUserGenerated('signal_telecommunication_double_data_table', stationData)) {
      setTableCode(dTable.station_info);
      dTable.station_info.station_name_english = updateTitleFn(dTable.station_info.station_name_english);
      dTable.station_info = { ...dTable.station_info };
      stationData.signal_telecommunication_double_data_table = { ...dTable };
    }
    const sTable = stationData?.signal_telecommunication_single_data_table;
    if (sTable?.station_info && !this.isTableUserGenerated('signal_telecommunication_single_data_table', stationData)) {
      setTableCode(sTable.station_info);
      sTable.station_info.station_name_english = updateTitleFn(sTable.station_info.station_name_english);
      sTable.station_info = { ...sTable.station_info };
      stationData.signal_telecommunication_single_data_table = { ...sTable };
    }
    const rTable = stationData?.signal_telecommunication_ref_sig_plan_table?.right_section;
    if (rTable?.station_info && !this.isTableUserGenerated('signal_telecommunication_ref_sig_plan_table', stationData)) {
      setTableCode(rTable.station_info);
      rTable.station_info.station_name_english = updateTitleFn(rTable.station_info.station_name_english);
      rTable.station_info = { ...rTable.station_info };
      stationData.signal_telecommunication_ref_sig_plan_table = { ...stationData.signal_telecommunication_ref_sig_plan_table };
    }
  }

  private validateStationIdUniqueness(stationData: any, oldId: string, newStationId: string): string | null {
    const list = this.getAdjacentStationsList(stationData);
    if (this.isCurrentStation(stationData, oldId)) {
      const dup = list.find(
        (s: any) => String(s?.station_id) === String(newStationId)
      );
      if (dup) {
        return `Station ID "${newStationId}" is already used by an adjacent station ("${dup.station_name || dup.station_id}"). Please enter a unique ID.`;
      }
    } else {
      const currentStationId = String(stationData?.['Station ID'] ?? '');
      if (currentStationId && String(newStationId) === currentStationId) {
        return `Station ID "${newStationId}" is already used by the current station. Please enter a unique ID.`;
      }
      const dup = list.find(
        (s: any) => String(s?.station_id) !== String(oldId) && String(s?.station_id) === String(newStationId)
      );
      if (dup) {
        return `Station ID "${newStationId}" is already used by another adjacent station ("${dup.station_name || dup.station_id}"). Please enter a unique ID.`;
      }
    }
    return null;
  }

  private validateStationCodeUniqueness(stationData: any, stationId: string, newCode: string): string | null {
    const currentCode = String(stationData?.['Station Code'] ?? '').trim().toLowerCase();
    const targetCode = newCode.trim().toLowerCase();
    if (currentCode && targetCode === currentCode) {
      return `Station Code "${newCode}" is already used by the current station.`;
    }
    const list = this.getAdjacentStationsList(stationData);
    const dup = list.find((s: any) => {
      if (String(s?.station_id) === String(stationId)) return false;
      return String(s?.station_code ?? '').trim().toLowerCase() === targetCode;
    });
    if (dup) {
      return `Station Code "${newCode}" is already used by another adjacent station.`;
    }
    return null;
  }

  /** Sync station_id change for the CURRENT station everywhere. */
  ssyncStationId(
    stationData: any,
    oldStationId: string,
    newStationId: string,
    alreadySynced = false
  ): void {
    if (!stationData || isNullOrUndefined(newStationId)) return;
    this.ensureStationMetaScopes(stationData, 'station_id', SCOPE_EVERYWHERE);
    this.addUpdateInputs(stationData);

    const currentStationId = stationData['Station ID'];
    this.setPimValue(stationData, 'pim_station_id', newStationId);
    stationData['Station ID'] = newStationId;
    stationData['station_id'] = newStationId;
    stationData.station_id = newStationId;

    this.updateTitleAssets(stationData, 'station_id', newStationId);

    stationData.Signal?.forEach((track: any) => {
      track.assets?.forEach((asset: any) => {
        if (String(asset.beside_station_id) === String(oldStationId) || String(asset.beside_station_id) === String(currentStationId)) {
          asset.beside_station_id = newStationId;
        }
      });
    });

    if (stationData.ip_port_table?.data && !this.isTableUserGenerated('ip_port_table', stationData)) {
      stationData.ip_port_table.data.forEach((row: any) => {
        if (String(row.station_id) === String(oldStationId) || String(row.station_id) === String(currentStationId) || this.isCurrentStation(stationData, row.station_id)) {
          row.station_id = newStationId;
        }
      });
      stationData.ip_port_table = { ...stationData.ip_port_table, data: [...stationData.ip_port_table.data] };
    }

    this.updateTowerFields(stationData, 'station_id', newStationId, (tower, key) => (
      String(tower.station_id) === String(oldStationId) ||
      String(tower.station_id) === String(currentStationId) ||
      this.isCurrentStation(stationData, tower.station_id) ||
      tower.tower_id === 1 ||
      Boolean(key && key.toUpperCase().includes('CURRENT'))
    ));

    if (stationData.station_tag_summary?.stations && !this.isTableUserGenerated('station_tag_summary', stationData)) {
      stationData.station_tag_summary.stations.forEach((row: any) => {
        if (String(row.station_id) === String(oldStationId) || String(row.station_id) === String(currentStationId) || this.isCurrentStation(stationData, row.station_id)) {
          row.station_id = newStationId;
        }
      });
      stationData.station_tag_summary = {
        ...stationData.station_tag_summary,
        stations: [...stationData.station_tag_summary.stations]
      };
    }

    const nearest = stationData?.nearest_stations_info;
    if (nearest) {
      if (String(nearest.left_station_id) === String(oldStationId)) nearest.left_station_id = newStationId;
      if (String(nearest.right_station_id) === String(oldStationId)) nearest.right_station_id = newStationId;
    }

    if (oldStationId && newStationId && String(oldStationId) !== String(newStationId)) {
      this.syncCommunicationFields(stationData, String(oldStationId), String(newStationId));
    }
    this.triggerRegeneration(stationData);

    this.syncSurveyStationData({ station_id: newStationId, 'Station ID': newStationId }, 'station_id_scope');

    this.syncToPeers(stationData, alreadySynced, (target) => {
      this.ssyncStationId(target, oldStationId, newStationId, true);
    });
  }

  /**
   * Apply Everywhere for an adjacent station ID: adjacent data, PIM value, arrows, towers and
   * system-generated tables (user-generated tables are skipped).
   */
  syncAdjacentStationId(
    stationData: any,
    newId: string,
    stationUuid?: string,
    oldId?: string,
    alreadySynced = false
  ): void {
    if (!stationData || isNullOrUndefined(newId)) return;

    const adj = this.findAdjacentByUuidOrId(stationData, stationUuid, oldId);
    const oldActualId = oldId || adj?.station_id;
    if (adj) {
      adj.pim_station_id = newId;
      adj.station_id = newId;
    }

    if (!this.isTableUserGenerated('ip_port_table', stationData)) {
      stationData?.ip_port_table?.data?.forEach((row: any) => {
        if (String(row.station_id) === String(oldActualId)) row.station_id = newId;
      });
    }

    stationData.Signal?.forEach((signal: any) => {
      signal.assets?.forEach((asset: any) => {
        if (String(asset.beside_station_id) === String(oldActualId)) asset.beside_station_id = newId;
      });
    });

    if (!this.isTableUserGenerated('station_tag_summary', stationData)) {
      stationData.station_tag_summary?.stations?.forEach((row: any) => {
        if (String(row.station_id) === String(oldActualId)) row.station_id = newId;
      });
    }

    this.updateTowerFields(stationData, 'station_id', newId, (tower) => (
      String(tower.station_id) === String(oldActualId) || String(tower.station_id) === String(adj?.station_id)
    ));

    const nearest = stationData?.nearest_stations_info;
    if (nearest) {
      if (String(nearest.left_station_id) === String(oldActualId)) nearest.left_station_id = newId;
      if (String(nearest.right_station_id) === String(oldActualId)) nearest.right_station_id = newId;
    }

    if (oldActualId && newId && String(oldActualId) !== String(newId)) {
      this.syncCommunicationFields(stationData, String(oldActualId), String(newId));
    }
    this.triggerRegeneration(stationData);

    this.syncToPeers(stationData, alreadySynced, (target) => {
      this.syncAdjacentStationId(target, newId, adj?.station_uuid, oldActualId, true);
    });
  }

  // Synchronize main station name across all tables and assets
  syncStationName(stationData: any, newStationName: string, oldStationName?: string, alreadySynced = false): void {
    if (!stationData || isNullOrUndefined(newStationName)) return;

    this.ensureStationMetaScopes(stationData, 'station_name', SCOPE_EVERYWHERE);
    this.addUpdateInputs(stationData);

    const effectiveOldName = oldStationName || stationData['Station Name'] || '';
    const stationCode = stationData['Station Code'] ?? '';
    const stationId   = stationData['Station ID'];
    const zone: string = stationData['Zone'] ?? '';

    this.setPimValue(stationData, 'pim_station_name', newStationName);
    stationData['Station Name'] = newStationName;
    stationData['station_name'] = newStationName;

    const updateTitleName = (currentTitle: string): string => {
      if (!currentTitle || typeof currentTitle !== 'string') return newStationName;
      const cleanNewName = newStationName.includes('(')
        ? newStationName.split('(')[0].trim()
        : newStationName.trim();
      if (currentTitle.includes('(')) {
        const match = currentTitle.match(/\([^)]*\)/);
        return `${cleanNewName} ${match ? match[0] : ''}`.trim();
      }
      return cleanNewName;
    };

    this.updateTitleAssets(stationData, 'station_name', newStationName, (item) => (
      !stationId || !item.station_id || String(item.station_id) === String(stationId)
    ));

    this.updateSignalAndAuthorTablesTitle(stationData, updateTitleName);

    this.updateTowerFields(stationData, 'station_name', newStationName, (tower, key) => (
      String(tower.station_id) === String(stationId) ||
      this.isCurrentStation(stationData, tower.station_id) ||
      tower.tower_id === 1 ||
      Boolean(key && key.toUpperCase().includes('CURRENT')) ||
      (effectiveOldName && String(tower.station_name ?? '').trim().toLowerCase() === effectiveOldName.trim().toLowerCase()) ||
      (stationCode && String(tower.station_code ?? '').trim().toLowerCase() === String(stationCode).trim().toLowerCase())
    ));

    const nameField = this.nameFieldForZone(zone, true, false);
    if (stationData.ip_port_table?.data && !this.isTableUserGenerated('ip_port_table', stationData)) {
      stationData.ip_port_table.data.forEach((row: any) => {
        if (String(row.station_id) === String(stationId) || this.isCurrentStation(stationData, row.station_id)) {
          row.station_name = newStationName;
        }
      });
      stationData.ip_port_table = {
        ...stationData.ip_port_table,
        data: [...stationData.ip_port_table.data]
      };
    }

    if (stationData.station_tag_summary?.stations && !this.isTableUserGenerated('station_tag_summary', stationData)) {
      stationData.station_tag_summary.stations.forEach((row: any) => {
        if (String(row.station_id) === String(stationId) || this.isCurrentStation(stationData, row.station_id)) {
          if (nameField === 'station_name') {
            row.name = newStationName;
          }
          row.station_name = newStationName;
        }
      });
      stationData.station_tag_summary = {
        ...stationData.station_tag_summary,
        stations: [...stationData.station_tag_summary.stations]
      };
    }

    if (effectiveOldName && newStationName && effectiveOldName !== newStationName) {
      this.syncCommunicationFields(stationData, effectiveOldName, newStationName);
    }
    this.triggerRegeneration(stationData);

    this.syncSurveyStationData({ station_name: newStationName, 'Station Name': newStationName }, 'station_name_scope');

    this.syncToPeers(stationData, alreadySynced, (target) => {
      this.syncStationName(target, newStationName, effectiveOldName, true);
    });
  }

  /**
   * Apply Everywhere for an adjacent station name: adjacent data, PIM value, arrows, towers, CM distances and
   * system-generated tables (user-generated tables are skipped).
   */
  syncAdjacentStationName(
    stationData: any,
    newStationName: string,
    stationUuid?: string,
    stationId?: string,
    oldStationName?: string,
    alreadySynced = false,
  ): void {
    if (!stationData || isNullOrUndefined(newStationName)) return;

    const adj = this.findAdjacentByUuidOrId(stationData, stationUuid, stationId);
    const oldName = oldStationName || adj?.station_name || '';
    if (adj) {
      adj.pim_station_name = newStationName;
      adj.station_name = newStationName;
    }

    if (!this.isTableUserGenerated('ip_port_table', stationData)) {
      stationData?.ip_port_table?.data?.forEach((row: any) => {
        if (String(row.station_id) === String(adj?.station_id)) {
          row.station_name = newStationName;
        }
      });
    }

    const nearest = stationData?.nearest_stations_info;
    if (nearest) {
      // The Kernex name under the arrow follows when it still shows the old name
      if (String(nearest.left_station_id) === String(adj?.station_id)) {
        nearest.left_station_name = newStationName;
        if (nearest.left_station_name_lidar === oldName) nearest.left_station_name_lidar = newStationName;
      }
      if (String(nearest.right_station_id) === String(adj?.station_id)) {
        nearest.right_station_name = newStationName;
        if (nearest.right_station_name_lidar === oldName) nearest.right_station_name_lidar = newStationName;
      }
    }

    this.updateTowerFields(stationData, 'station_name', newStationName, (tower) => (
      String(tower.station_id) === String(adj?.station_id) ||
      (oldName && String(tower.station_name ?? '').trim().toLowerCase() === oldName.trim().toLowerCase()) ||
      (adj?.station_name && String(tower.station_name ?? '').trim().toLowerCase() === String(adj.station_name).trim().toLowerCase()) ||
      (adj?.station_code && String(tower.station_code ?? '').trim().toLowerCase() === String(adj.station_code).trim().toLowerCase())
    ));

    const maxCmMap = stationData?.max_cm_distances_map;
    if (Array.isArray(maxCmMap) && oldName) {
      const oldNameToMatch = oldName.trim().toLowerCase();
      maxCmMap.forEach((entry: any) => {
        if (entry?.station_name?.trim()?.toLowerCase() === oldNameToMatch) {
          entry.station_name = newStationName;
        }
      });
    }

    if (!this.isTableUserGenerated('station_tag_summary', stationData)) {
      stationData?.station_tag_summary?.stations?.forEach((entry: any) => {
        if (String(entry.station_id) === String(adj?.station_id)) {
          entry.name = newStationName;
          entry.station_name = newStationName;
        }
      });
    }

    if (oldName && newStationName && oldName !== newStationName) {
      this.syncCommunicationFields(stationData, oldName, newStationName);
    }
    this.triggerRegeneration(stationData);

    this.syncToPeers(stationData, alreadySynced, (target) => {
      this.syncAdjacentStationName(target, newStationName, adj?.station_uuid, adj?.station_id, oldName, true);
    });
  }

  // Synchronize main station code across all tables and assets
  syncStationCode(stationData: any, newStationCode: string, oldStationCode?: string, alreadySynced = false): void {
    if (!stationData || isNullOrUndefined(newStationCode)) return;
    this.ensureStationMetaScopes(stationData, 'station_code', SCOPE_EVERYWHERE);
    this.addUpdateInputs(stationData);

    const effectiveOldCode = oldStationCode || stationData['Station Code'] || '';
    const stationId = stationData['Station ID'];
    const zone: string = stationData['Zone'] ?? '';

    this.setPimValue(stationData, 'pim_station_code', newStationCode);
    stationData['Station Code'] = newStationCode;
    stationData['station_code'] = newStationCode;

    const updateTitleCode = (currentTitle: string): string => {
      if (!currentTitle || typeof currentTitle !== 'string') return currentTitle;
      if (currentTitle.includes('(')) {
        const baseName = currentTitle.split('(')[0].trim();
        return `${baseName} (${newStationCode})`.trim();
      }
      return currentTitle;
    };

    this.updateTitleAssets(stationData, 'station_code', newStationCode, (item) => (
      !stationId || !item.station_id || String(item.station_id) === String(stationId)
    ));

    this.updateSignalAndAuthorTablesTitle(stationData, updateTitleCode, newStationCode);

    if (effectiveOldCode && newStationCode && effectiveOldCode !== newStationCode) {
      if (typeof stationData['REf RFID Tag Layout No'] === 'string') {
        stationData['REf RFID Tag Layout No'] = stationData['REf RFID Tag Layout No'].replace(
          new RegExp(`_${effectiveOldCode}_`, 'gi'),
          `_${newStationCode}_`
        );
      }
      if (stationData.author_review_table && typeof stationData.author_review_table.layout_version === 'string' &&
          !this.isTableUserGenerated('author_table', stationData)) {
        stationData.author_review_table.layout_version = stationData.author_review_table.layout_version.replace(
          new RegExp(`_${effectiveOldCode}_`, 'gi'),
          `_${newStationCode}_`
        );
      }
    }

    const nameField = this.nameFieldForZone(zone, true, false);
    if (stationData.ip_port_table?.data && !this.isTableUserGenerated('ip_port_table', stationData)) {
      stationData.ip_port_table.data.forEach((row: any) => {
        if (String(row.station_id) === String(stationId) || this.isCurrentStation(stationData, row.station_id)) {
          row.station_code = newStationCode;
        }
      });
    }

    this.updateTowerFields(stationData, 'station_code', newStationCode, (tower, key) => (
      String(tower.station_id) === String(stationId) ||
      this.isCurrentStation(stationData, tower.station_id) ||
      tower.tower_id === 1 ||
      Boolean(key && key.toUpperCase().includes('CURRENT')) ||
      (effectiveOldCode && String(tower.station_code ?? '').trim().toLowerCase() === effectiveOldCode.trim().toLowerCase())
    ));

    if (stationData.station_tag_summary?.stations && !this.isTableUserGenerated('station_tag_summary', stationData)) {
      stationData.station_tag_summary.stations.forEach((row: any) => {
        if (String(row.station_id) === String(stationId) || this.isCurrentStation(stationData, row.station_id)) {
          row.station_code = newStationCode;
          if (nameField === 'station_code') {
            row.name = newStationCode;
          }
        }
      });
      stationData.station_tag_summary = {
        ...stationData.station_tag_summary,
        stations: [...stationData.station_tag_summary.stations]
      };
    }

    if (effectiveOldCode && newStationCode && effectiveOldCode !== newStationCode) {
      this.syncCommunicationFields(stationData, effectiveOldCode, newStationCode);
    }
    this.triggerRegeneration(stationData);

    this.syncSurveyStationData({ station_code: newStationCode, 'Station Code': newStationCode }, 'station_code_scope');

    this.syncToPeers(stationData, alreadySynced, (target) => {
      this.syncStationCode(target, newStationCode, effectiveOldCode, true);
    });
  }

  /**
   * Apply Everywhere for an adjacent station code: adjacent data, PIM value and
   * system-generated tables (user-generated tables are skipped).
   */
  syncAdjacentStationCode(
    stationData: any,
    newStationCode: string,
    stationUuid?: string,
    stationId?: string,
    oldStationCode?: string,
    alreadySynced = false
  ): void {
    if (!stationData || isNullOrUndefined(newStationCode)) return;

    const zone: string = stationData['Zone'] ?? '';
    const adj = this.findAdjacentByUuidOrId(stationData, stationUuid, stationId);
    if (!adj) return;

    const oldCode = oldStationCode || adj.station_code || '';
    adj.pim_station_code = newStationCode;
    adj.station_code = newStationCode;

    if (!this.isTableUserGenerated('ip_port_table', stationData)) {
      stationData?.ip_port_table?.data?.forEach((row: any) => {
        if (String(row.station_id) === String(adj.station_id)) {
          row.station_code = newStationCode;
        }
      });
    }

    this.updateTowerFields(stationData, 'station_code', newStationCode, (tower) => (
      String(tower.station_id) === String(adj.station_id) ||
      (oldCode && String(tower.station_code ?? '').trim().toLowerCase() === oldCode.trim().toLowerCase()) ||
      (adj.station_name && String(tower.station_name ?? '').trim().toLowerCase() === String(adj.station_name).trim().toLowerCase())
    ));

    if (!this.isTableUserGenerated('station_tag_summary', stationData)) {
      stationData?.station_tag_summary?.stations?.forEach((entry: any) => {
        if (
          (adj.station_uuid && entry.station_uuid === adj.station_uuid) ||
          (adj.station_id && String(entry.station_id) === String(adj.station_id))
        ) {
          entry.station_code = newStationCode;
          const tagSummaryDisplayedField = this.nameFieldForZone(zone, false, adj.is_block_section_station ?? false);
          if (tagSummaryDisplayedField === 'station_code') {
            entry.name = newStationCode;
          }
        }
      });
    }

    if (oldCode && newStationCode && oldCode !== newStationCode) {
      this.syncCommunicationFields(stationData, oldCode, newStationCode);
    }
    this.triggerRegeneration(stationData);

    this.syncToPeers(stationData, alreadySynced, (target) => {
      this.syncAdjacentStationCode(target, newStationCode, adj.station_uuid, adj.station_id, oldCode, true);
    });
  }

  syncStationTitle(stationData: any, newTitle: string, alreadySynced = false): void {
    if (!stationData || isNullOrUndefined(newTitle)) return;

    this.ensureStationMetaScopes(stationData, 'station_title', SCOPE_EVERYWHERE);
    this.addUpdateInputs(stationData);

    stationData['Station Title'] = newTitle;
    stationData['station_title'] = newTitle;

    this.updateTitleAssets(stationData, 'station_title', newTitle);

    if (stationData.author_review_table?.station && !this.isTableUserGenerated('author_table', stationData)) {
      stationData.author_review_table.station.station_title = newTitle;
    }

    this.syncSurveyStationData({ station_title: newTitle, 'Station Title': newTitle }, 'station_title_scope');
    this.triggerRegeneration(stationData);

    this.syncToPeers(stationData, alreadySynced, (target) => {
      this.syncStationTitle(target, newTitle, true);
    });
  }

  syncSection(stationData: any, newSection: string, alreadySynced = false): void {
    if (!stationData || isNullOrUndefined(newSection)) return;

    stationData['Section'] = newSection;
    stationData['section'] = newSection;
    if ('section_type' in stationData) {
      stationData['section_type'] = newSection;
    }

    if (stationData.author_review_table?.station) {
      stationData.author_review_table.station.section = newSection;
    }

    if (stationData.signal_telecommunication_single_data_table?.station_info) {
      stationData.signal_telecommunication_single_data_table.station_info.section = newSection;
    }

    if (stationData.signal_telecommunication_double_data_table?.station_info) {
      stationData.signal_telecommunication_double_data_table.station_info.section = newSection;
    }

    if (this.stepperService.surveyData?.stationData) {
      this.stepperService.surveyData.stationData.section = newSection;
      (this.stepperService.surveyData.stationData as any).section_name = newSection;
    }

    this.triggerRegeneration(stationData);

    this.syncToPeers(stationData, alreadySynced, (target) => {
      this.syncSection(target, newSection, true);
    });
  }

  /**
   * Deep-walk the entire stationData object and replace occurrences of
   * oldValue with newValue inside ANY string property — including
   * embedded occurrences like "BMM EXIT &<br>LPRA ENTRY BORDER",
   * block section names like "BHASKARPARA-LILAPUR ROADA",
   * block section IDs like "25502-25603", comm_start, comm_end, etc.
   *
   * Uses word-boundary matching (\b) so that "DEV123" won't accidentally
   * match inside "DEV1234" — only at real boundaries (spaces, hyphens,
  /**
   * Replaces occurrences of an old station token (name, code, or ID)
   * with the new token across all border tags, tags, and communication
   * structures in stationData:
   * - type_of_communication_point
   * - comm_start
   * - comm_end
   * - communication_point_name
   * - comm_arrow_config.arrow_label_1 / arrow_label_2
   * - tag.modified comm fields
   */
  syncCommunicationFields(stationData: any, oldValue: string, newValue: string): void {
    const oldNorm = String(oldValue ?? '').trim();
    const newNorm = String(newValue ?? '').trim();
    if (!oldNorm || !newNorm || oldNorm.toLowerCase() === newNorm.toLowerCase()) {
      return;
    }

    const replaceToken = (text: string): string => {
      if (!text || typeof text !== 'string') return text;
      const escaped = oldNorm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(?<![a-zA-Z0-9])${escaped}(?![a-zA-Z0-9])`, 'gi');
      return text.replace(regex, (match) => {
        if (match === match.toUpperCase() && match !== match.toLowerCase()) {
          return newNorm.toUpperCase();
        }
        if (match.length > 1 && match[0] === match[0].toUpperCase() && match.slice(1) === match.slice(1).toLowerCase()) {
          return newNorm.charAt(0).toUpperCase() + newNorm.slice(1);
        }
        return newNorm;
      });
    };

    const updateTag = (tag: any) => {
      if (!tag || typeof tag !== 'object') return;
      let changed = false;

      const updateStringProp = (obj: any, prop: string) => {
        if (obj && typeof obj[prop] === 'string' && obj[prop].trim()) {
          const updated = replaceToken(obj[prop]);
          if (updated !== obj[prop]) {
            obj[prop] = updated;
            changed = true;
          }
        }
      };

      const COMM_PROPS = [
        'type_of_communication_point', 'comm_start', 'comm_end',
        'communication_point_name', 'communication_label_name',
        'communication_bi_directional_name', 'display_communication_point',
        'arrow_label_1', 'arrow_label_2'
      ];
      const updateObjCommProps = (obj: any) => {
        if (!obj || typeof obj !== 'object') return;
        COMM_PROPS.forEach(p => updateStringProp(obj, p));
        if (obj.comm_arrow_config && typeof obj.comm_arrow_config === 'object') {
          for (const k of Object.keys(obj.comm_arrow_config)) {
            if (
              typeof obj.comm_arrow_config[k] === 'string' &&
              !k.includes('direction') &&
              !k.includes('position') &&
              !k.includes('color')
            ) {
              updateStringProp(obj.comm_arrow_config, k);
            }
          }
        }
      };

      const updateMetaDataDependencies = (deps: any) => {
        if (!deps || typeof deps !== 'object') return;
        for (const depKey of Object.keys(deps)) {
          const dep = deps[depKey];
          if (dep && typeof dep === 'object') {
            updateStringProp(dep, 'asset_id');
            updateStringProp(dep, 'type');
          }
        }
      };

      // 1. Direct tag communication properties and metadata dependencies
      updateObjCommProps(tag);
      updateMetaDataDependencies(tag.meta_data_dependencies);

      // 2. Embedded communicationPoint if present on tag
      if (tag.communicationPoint && typeof tag.communicationPoint === 'object') {
        updateObjCommProps(tag.communicationPoint);
        updateMetaDataDependencies(tag.communicationPoint.meta_data_dependencies);
      }

      // 3. Modified snapshot if present on tag
      if (tag.modified && typeof tag.modified === 'object') {
        updateObjCommProps(tag.modified);
        updateMetaDataDependencies(tag.modified.meta_data_dependencies);
      }

      // 4. _commBorderTag reference
      if (tag._commBorderTag && typeof tag._commBorderTag === 'object') {
        updateTag(tag._commBorderTag);
      }

      if (changed) {
        tag.tag_changed = true;
      }
    };

    // Helper to traverse a tags map (like stationData.Tags or stationData.unused_tags)
    const traverseTagsContainer = (container: any) => {
      if (!container || typeof container !== 'object') return;
      for (const key of Object.keys(container)) {
        const val = container[key];
        if (Array.isArray(val)) {
          for (const item of val) {
            if (item && typeof item === 'object') {
              if (Array.isArray(item.assets)) {
                for (const asset of item.assets) {
                  updateTag(asset);
                }
              } else {
                updateTag(item);
              }
            }
          }
        }
      }
    };

    // 1. Iterate through stationData.Tags and stationData.tags
    traverseTagsContainer(stationData?.Tags);
    traverseTagsContainer(stationData?.tags);

    // 2. Iterate through stationData.unused_tags
    traverseTagsContainer(stationData?.unused_tags);

    // 3. Iterate through input_border_tags_data and metadata.input_border_tags_data
    const checkBorderTracks = (tracks: any) => {
      if (Array.isArray(tracks)) {
        for (const trackEntry of tracks) {
          if (Array.isArray(trackEntry?.manual_border_tags)) {
            for (const tag of trackEntry.manual_border_tags) {
              updateTag(tag);
            }
          }
        }
      }
    };
    checkBorderTracks(stationData?.input_border_tags_data);
    checkBorderTracks(stationData?.metadata?.input_border_tags_data);

    // 4. Iterate through border_tags and manual_border_tags
    if (Array.isArray(stationData?.border_tags)) {
      for (const tag of stationData.border_tags) {
        updateTag(tag);
      }
    }
    if (Array.isArray(stationData?.manual_border_tags)) {
      for (const tag of stationData.manual_border_tags) {
        updateTag(tag);
      }
    }

    // 5. Iterate through stationData['Communication Points'] and communication_points
    for (const key of ['Communication Points', 'communication_points']) {
      const commPoints = stationData?.[key];
      if (Array.isArray(commPoints)) {
        for (const cp of commPoints) {
          updateTag(cp);
        }
      }
    }

    // 6. Iterate through computed_communication_points
    if (Array.isArray(stationData?.computed_communication_points)) {
      for (const cp of stationData.computed_communication_points) {
        updateTag(cp);
        if (cp && typeof cp === 'object') {
          for (const key of Object.keys(cp)) {
            if (typeof cp[key] === 'string') {
              cp[key] = replaceToken(cp[key]);
            }
          }
        }
      }
    }

    // 7. Iterate through stationData.Signal and stationData.Signals
    for (const key of ['Signal', 'Signals']) {
      const signalsList = stationData?.[key];
      if (Array.isArray(signalsList)) {
        for (const track of signalsList) {
          if (Array.isArray(track?.assets)) {
            for (const asset of track.assets) {
              updateTag(asset);
            }
          }
        }
      }
    }

    // 8. Iterate through visual canvas communication labels in ymp_data
    if (Array.isArray(stationData?.ymp_data)) {
      // Loop through each visual element
      for (const item of stationData.ymp_data) {
        // Check if item contains text string
        if (item && typeof item === 'object' && typeof item.text === 'string') {
          // Replace token in label text
          item.text = replaceToken(item.text);
        }
      }
    }

    // 9. Iterate through revision history notes
    if (Array.isArray(stationData?.revision_history)) {
      // Loop through revision history entries
      for (const entry of stationData.revision_history) {
        // Check if notes array exists
        if (entry && Array.isArray(entry.notes)) {
          // Loop through each note
          for (let i = 0; i < entry.notes.length; i++) {
            // Check if note is string
            if (typeof entry.notes[i] === 'string') {
              // Replace token in note string
              entry.notes[i] = replaceToken(entry.notes[i]);
            }
          }
        }
      }
    }
  }


  private triggerRegeneration(stationData: any): void {
    this.addUpdateInputs(stationData);
  }

  /**
   * Deep-clone stationData for use as a rollback snapshot.
   * Callers should call this BEFORE any handler, then call restoreStationData()
   * if the downstream API call fails.
   */
  snapshotStationData(stationData: any): any {
    return structuredClone(stationData);
  }

  /**
   * Restore all properties of stationData from a previously taken snapshot.
   * Call this in the API error handler to undo all sync mutations.
   */
  restoreStationData(stationData: any, snapshot: any): void {
    // Remove keys not in snapshot
    Object.keys(stationData).forEach(k => {
      if (!(k in snapshot)) delete stationData[k];
    });
    // Restore all keys from snapshot (deep assign)
    Object.assign(stationData, structuredClone(snapshot));
  }

  /**
   * Returns true if change was applied, false if a local duplicate was detected
   * (no mutations occur in that case).
   */
  handleIpPortStationIdChange(
    stationData: any,
    row: any,
    newStationId: string,
    scope: SyncScopeChoice = SCOPE_EVERYWHERE
  ): true | string {
    const oldId = row.station_id;
    if (String(oldId) === String(newStationId)) return true;

    const dupError = this.validateStationIdUniqueness(stationData, oldId, newStationId);
    if (dupError) return dupError;

    // ── Apply changes ──
    const isCurrent = this.isCurrentStation(stationData, oldId);
    const adj = isCurrent ? null : this.findAdjacentById(stationData, oldId);

    if (scope === SCOPE_HERE) {
      row.station_id = newStationId;
      if (adj) {
        adj.station_id_scope = SCOPE_HERE;
      }
      row.station_id_scope = SCOPE_HERE;
      if (stationData) {
        this.ensureStationMetaScopes(stationData, 'station_id', SCOPE_HERE);
      }
      this.removeUpdateInputs(stationData);
      this.stationDetailsUpdated$.next();
      return true;
    }

    if (isCurrent) {
      this.ssyncStationId(stationData, oldId, newStationId);
    } else {
      this.syncAdjacentStationId(stationData, newStationId, adj?.station_uuid, oldId);
    }
    row.station_id = newStationId;
    if (stationData) {
      this.ensureStationMetaScopes(stationData, 'station_id', SCOPE_EVERYWHERE);
    }
    this.triggerRegeneration(stationData);
    return true;
  }

  /**
   * Returns true if change was applied, string error if a duplicate was detected.
   *
   * KEY RULE: The ip_port_table shows station_code for WR, station_name otherwise.
   * We only sync the field that THIS table is actually displaying — never blindly
   * sync both name and code together across tables.
   */
  handleIpPortStationNameChange(
    stationData: any,
    row: any,
    newValue: string,
    scope: SyncScopeChoice = SCOPE_EVERYWHERE
  ): true | string {
    const isCurrent = this.isCurrentStation(stationData, row.station_id);
    const adj = isCurrent ? null : this.findAdjacentById(stationData, row.station_id);

    const zone: string = stationData['Zone'] ?? '';
    const isBlock = adj?.is_block_section_station ?? false;
    const nameField = this.nameFieldForZone(zone, isCurrent, isBlock);

    if (scope === SCOPE_HERE) {
      row.station_name = newValue;
      row.station_code = newValue;
      const scopeProp = `${nameField}_scope`;
      if (adj) {
        adj[scopeProp] = SCOPE_HERE;
      }
      row[scopeProp] = SCOPE_HERE;
      if (stationData) {
        this.ensureStationMetaScopes(stationData, nameField, SCOPE_HERE);
      }
      this.removeUpdateInputs(stationData);
      this.stationDetailsUpdated$.next();
      return true;
    }

    const oldVal = (nameField === 'station_code') ? (row.station_code ?? '') : (row.station_name ?? '');

    if (nameField === 'station_code') {
      if (isCurrent) {
        this.syncStationCode(stationData, newValue, oldVal);
      } else {
        const stationUuid = adj?.station_uuid;
        this.syncAdjacentStationCode(stationData, newValue, stationUuid, row.station_id, oldVal);
      }
      row.station_code = newValue;
      row.station_name = newValue;
    } else {
      if (isCurrent) {
        this.syncStationName(stationData, newValue, oldVal);
      } else {
        const stationUuid = adj?.station_uuid;
        this.syncAdjacentStationName(stationData, newValue, stationUuid, row.station_id, oldVal);
      }
      row.station_name = newValue;
    }
    if (stationData) {
      this.ensureStationMetaScopes(stationData, nameField, SCOPE_EVERYWHERE);
    }
    this.triggerRegeneration(stationData);
    return true;
  }

  handleIpPortStationCodeChange(
    stationData: any,
    row: any,
    newValue: string,
    scope: SyncScopeChoice = SCOPE_EVERYWHERE
  ): true | string {
    const isCurrent = this.isCurrentStation(stationData, row.station_id);
    const adj = isCurrent ? null : this.findAdjacentById(stationData, row.station_id);

    if (!isCurrent) {
      const dupError = this.validateStationCodeUniqueness(stationData, row.station_id, newValue);
      if (dupError) return dupError;
    }

    // ── Apply changes — strictly sync station_code ──
    if (scope === SCOPE_HERE) {
      row.station_name = newValue;
      row.station_code = newValue;
      if (stationData) {
        this.ensureStationMetaScopes(stationData, 'station_code', SCOPE_HERE);
      }
      this.removeUpdateInputs(stationData);
      this.stationDetailsUpdated$.next();
      return true;
    }

    const oldCode = row.station_code ?? '';
    if (isCurrent) {
      this.syncStationCode(stationData, newValue, oldCode);
    } else {
      const stationUuid = adj?.station_uuid;
      this.syncAdjacentStationCode(stationData, newValue, stationUuid, row.station_id, oldCode);
    }
    row.station_code = newValue;
    if (stationData) {
      this.ensureStationMetaScopes(stationData, 'station_code', SCOPE_EVERYWHERE);
    }
    this.triggerRegeneration(stationData);
    return true;
  }

  /**
   * Returns true if change was applied, false if a local duplicate was detected.
   */
  handleTagTableStationIdChange(
    stationData: any,
    st: any,
    newStationId: string,
    scope: SyncScopeChoice = SCOPE_EVERYWHERE
  ): true | string {
    const oldId = st.station_id;
    if (String(oldId) === String(newStationId)) return true;

    const dupError = this.validateStationIdUniqueness(stationData, oldId, newStationId);
    if (dupError) return dupError;

    // ── Apply changes ──
    const isCurrent = this.isCurrentStation(stationData, oldId);
    const adj = isCurrent ? null : this.findAdjacentByUuidOrId(stationData, st?.station_uuid, oldId);

    if (scope === SCOPE_HERE) {
      st.station_id = newStationId;
      if (adj) {
        adj.station_id_scope = SCOPE_HERE;
      }
      st.station_id_scope = SCOPE_HERE;
      if (stationData) {
        this.ensureStationMetaScopes(stationData, 'station_id', SCOPE_HERE);
      }
      this.removeUpdateInputs(stationData);
      this.stationDetailsUpdated$.next();
      return true;
    }

    if (isCurrent) {
      this.ssyncStationId(stationData, oldId, newStationId);
    } else {
      this.syncAdjacentStationId(stationData, newStationId, st.station_uuid, oldId);
    }
    st.station_id = newStationId;
    if (stationData) {
      this.ensureStationMetaScopes(stationData, 'station_id', SCOPE_EVERYWHERE);
    }
    this.triggerRegeneration(stationData);
    return true;
  }

  /**
   * Returns true if change was applied, string error if a duplicate was detected.
   *
   * KEY RULE: The tag summary table shows station_code or station_name depending
   * on zone/isBlock. We only sync the field that THIS table is actually displaying.
   * Editing station_name in the tag table must NOT update station_code in the ip_port
   * table, and vice versa.
   */
  handleTagTableNameChange(
    stationData: any,
    st: any,
    newName: string,
    scope: SyncScopeChoice = SCOPE_EVERYWHERE
  ): true | string {
    // Check if station is current station
    const isCurrent = this.isCurrentStation(stationData, st.station_id);
    // Find adjacent station canonically by UUID or ID
    const adj = isCurrent ? null : this.findAdjacentByUuidOrId(stationData, st.station_uuid, st.station_id);

    // Resolve railway zone
    const zone: string = stationData['Zone'] ?? '';
    // Determine if block section station
    const isBlock = st?.is_block_section_station ?? adj?.is_block_section_station ?? false;
    // Resolve displayed field name
    const nameField = this.nameFieldForZone(zone, isCurrent, isBlock);

    // Handle here-only scope
    if (scope === SCOPE_HERE) {
      st.name = newName;
      st.station_name = newName;
      st.station_code = newName;
      const scopeProp = `${nameField}_scope`;
      if (adj) {
        adj[scopeProp] = SCOPE_HERE;
      }
      st[scopeProp] = SCOPE_HERE;
      if (stationData) {
        this.ensureStationMetaScopes(stationData, nameField, SCOPE_HERE);
      }
      this.removeUpdateInputs(stationData);
      this.stationDetailsUpdated$.next();
      return true;
    }

    const oldVal = (nameField === 'station_code') ? (st.station_code ?? st.name ?? '') : (st.station_name || st.name || '');

    if (nameField === 'station_code') {
      if (isCurrent) {
        this.syncStationCode(stationData, newName, oldVal);
      } else {
        const stationUuid = adj?.station_uuid || st.station_uuid;
        this.syncAdjacentStationCode(stationData, newName, stationUuid, st.station_id, oldVal);
      }
      st.station_code = newName;
    } else {
      if (isCurrent) {
        this.syncStationName(stationData, newName, oldVal);
      } else {
        const stationUuid = adj?.station_uuid || st.station_uuid;
        this.syncAdjacentStationName(stationData, newName, stationUuid, st.station_id, oldVal);
      }
      st.station_name = newName;
    }
    st.name = newName;
    if (stationData) {
      this.ensureStationMetaScopes(stationData, nameField, SCOPE_EVERYWHERE);
    }
    this.triggerRegeneration(stationData);
    return true;
  }

  handleTagTableCodeChange(
    stationData: any,
    st: any,
    newCode: string,
    scope: SyncScopeChoice = SCOPE_EVERYWHERE
  ): true | string {
    const isCurrent = this.isCurrentStation(stationData, st.station_id);
    const adj = isCurrent ? null : this.findAdjacentById(stationData, st.station_id);

    if (!isCurrent) {
      const dupError = this.validateStationCodeUniqueness(stationData, st.station_id, newCode);
      if (dupError) return dupError;
    }

    // ── Apply changes — strictly sync station_code ──
    if (scope === SCOPE_HERE) {
      st.name = newCode;
      st.station_name = newCode;
      st.station_code = newCode;
      if (stationData) {
        this.ensureStationMetaScopes(stationData, 'station_code', SCOPE_HERE);
      }
      this.removeUpdateInputs(stationData);
      this.stationDetailsUpdated$.next();
      return true;
    }

    const oldCode = st.station_code || st.name || '';
    if (isCurrent) {
      this.syncStationCode(stationData, newCode, oldCode);
    } else {
      const stationUuid = adj?.station_uuid;
      this.syncAdjacentStationCode(stationData, newCode, stationUuid, st.station_id, oldCode);
    }
    st.name = newCode;
    st.station_code = newCode;
    if (stationData) {
      this.ensureStationMetaScopes(stationData, 'station_code', SCOPE_EVERYWHERE);
    }
    this.triggerRegeneration(stationData);
    return true;
  }

  /**
   * Cleans a division string by stripping comma-separated zone/railway suffixes,
   * redundant "division" keywords, and excessive whitespace.
   */
  static cleanDivisionName(rawDivision: any): string {
    if (typeof rawDivision !== 'string' || !rawDivision.trim()) return '';
    if (rawDivision.includes(',') || /\bdivision\b/i.test(rawDivision)) {
      return rawDivision.split(',')[0].replace(/\bdivision\b/gi, '').trim();
    }
    return rawDivision.trim();
  }

  cleanDivisionName(rawDivision: any): string {
    return StationDetailsSyncService.cleanDivisionName(rawDivision);
  }

  /**
   * Sanitizes target['Station Division'] in-place if present and composite/corrupted.
   */
  static sanitizeStationDivision(target: any): void {
    if (!target) return;
    const clean = StationDetailsSyncService.cleanDivisionName(target['Station Division']);
    if (clean && clean !== target['Station Division']) {
      target['Station Division'] = clean;
    }
  }

  sanitizeStationDivision(target: any): void {
    StationDetailsSyncService.sanitizeStationDivision(target);
  }

  /**
   * Formats a clean division name for the Author Review Table by preserving
   * the existing casing of the 'Division' keyword and any trailing zone details.
   */
  static formatAuthorTableDivision(value: string, existingAuthorDivision?: string): string {
    if (!existingAuthorDivision) {
      const cleanVal = StationDetailsSyncService.cleanDivisionName(value) || String(value ?? '').trim();
      return cleanVal ? `${cleanVal} Division` : '';
    }
    const divisionParts = existingAuthorDivision.split(',');
    const rest = divisionParts.slice(1).join(',');
    const existingDivisionWord = divisionParts[0]?.match(/\bdivision\b/i)?.[0] || 'Division';
    const cleanVal = StationDetailsSyncService.cleanDivisionName(value) || String(value ?? '').trim();
    const divisionLabel = cleanVal ? `${cleanVal} ${existingDivisionWord}` : '';
    return rest ? `${divisionLabel},${rest}` : divisionLabel;
  }

  formatAuthorTableDivision(value: string, existingAuthorDivision?: string): string {
    return StationDetailsSyncService.formatAuthorTableDivision(value, existingAuthorDivision);
  }

  private plainSipText(value: any): string {
    if (value == null) return '';
    if (Array.isArray(value?.segments)) {
      const hasOldText = value.segments.some((s: any) => s.styleId === 'success');
      const hasNewText = value.segments.some((s: any) => s.styleId === 'danger');
      const segments = (hasOldText && hasNewText)
        ? value.segments.filter((s: any) => s.styleId !== 'success')
        : value.segments;
      return segments.map((s: any) => s.text).join('');
    }
    return String(value);
  }

  syncSipReference(stationData: any, value: any): void {
    if (!stationData) return;
    const plainSip = this.plainSipText(value);
    const sipNumber = plainSip?.substring(plainSip?.lastIndexOf(':') + 1)?.replace('SG(SP)', '')?.trim();

    if (stationData.author_review_table?.ref) stationData.author_review_table.ref.sip_value = plainSip;
    stationData['REF SIP'] = plainSip;

    const doubleTable = stationData.signal_telecommunication_double_data_table;
    if (doubleTable?.top_left_info) doubleTable.top_left_info.sip = sipNumber;
    if (doubleTable?.sheet_info?.[1]?.systemRef) doubleTable.sheet_info[1].systemRef.sip = sipNumber;
    if (doubleTable?.left_panel?.lines?.[1] != null) doubleTable.left_panel.lines[1] = `S.I.P. ${sipNumber}`;

    const singleTable = stationData.signal_telecommunication_single_data_table;
    if (singleTable?.sheet_info?.[1]?.kavachRef) singleTable.sheet_info[1].kavachRef.value = sipNumber ? `SIP:${sipNumber}` : '';

    const refSigRight = stationData.signal_telecommunication_ref_sig_plan_table?.left_section?.header?.right;
    if (refSigRight) refSigRight.ref_sig_plan = plainSip;
  }


  syncSipReferenceFromNumber(stationData: any, rawValue: string): void {
    const plain = String(rawValue ?? '')?.replace(/^SIP:?\s*/i, '')?.trim();
    this.syncSipReference(stationData, plain ? `SIP : ${plain}` : '');
  }
}